import React, { useState } from 'react';
// } from 'react';
import SearchableSelect from './SearchableSelect';
import { setLensSelection, LensData } from '../utils/lensStore';
import { PDMeasurementPopup } from './PDMeasurementPopup';
import { Camera } from 'lucide-react';
import { storePrescriptionFile } from '../utils/prescriptionStore';

type LensType = 'normal' | 'single_vision' | 'progressive' | 'bifocal' | '';
type CoatingType = 'normal' | 'anti_glare' | 'blue_light_filter' | 'uv_protection' | '';

interface EyePowerValue { sphere: string; cylinder: string; axis: string; }
interface EyePower { leftEye: EyePowerValue; rightEye: EyePowerValue; }

const LENS_TYPES: LensType[] = ['normal', 'single_vision', 'progressive', 'bifocal'];
const COATING_TYPES: CoatingType[] = ['normal', 'anti_glare', 'blue_light_filter', 'uv_protection'];
const STEPS = ['Lens Type', 'Eye Power', 'Coating'];

const SPHERE_OPTIONS = Array.from({ length: 161 }, (_, i) => {
  const v = (i - 80) / 4;
  return v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2);
});
const CYLINDER_OPTIONS = Array.from({ length: 49 }, (_, i) => {
  const v = (i - 24) / 4;
  return v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2);
});
const AXIS_OPTIONS = Array.from({ length: 180 }, (_, i) => String(i + 1));

const lensLabels: Record<string, string> = {
  normal: 'Normal (No Power)',
  single_vision: 'Single Vision',
  progressive: 'Progressive',
  bifocal: 'Bifocal',
};

const coatingLabels: Record<string, string> = {
  normal: 'No Coating',
  anti_glare: 'Anti Glare',
  blue_light_filter: 'Blue Light Filter',
  uv_protection: 'UV Protection',
};

const COATING_PRICES: Record<string, number> = {
  normal: 999,
  anti_glare: 899,
  blue_light_filter: 1090,
  uv_protection: 1500,
};

interface Props {
  cartItemId: string;
  productName: string;
  initialData?: LensData | null;
  onClose: () => void;
}

const LensSelectionModal: React.FC<Props> = ({ cartItemId, productName, initialData, onClose }) => {
  const [step, setStep] = useState(1);
  const [lensType, setLensType] = useState<LensType>((initialData?.lensType as LensType) || '');
  const [coating, setCoating] = useState<CoatingType>((initialData?.coating as CoatingType) || '');
  const [eyePower, setEyePower] = useState<EyePower>(
    initialData?.eyePower || {
      leftEye: { sphere: '', cylinder: '', axis: '' },
      rightEye: { sphere: '', cylinder: '', axis: '' },
    }
  );
  const [prescriptionFile, setPrescriptionFile] = useState<File | null>(null);
  const [isPdPopupOpen, setIsPdPopupOpen] = useState<boolean>(false);
  const [pdMeasurement, setPdMeasurement] = useState<string>(initialData?.pdMeasurement || localStorage.getItem('measuredPD') || '');
  const [leftPdMeasurement, setLeftPdMeasurement] = useState<string>(initialData?.leftPdMeasurement || localStorage.getItem('measuredLeftPD') || '');
  const [rightPdMeasurement, setRightPdMeasurement] = useState<string>(initialData?.rightPdMeasurement || localStorage.getItem('measuredRightPD') || '');


  const handleLensTypeSelect = (type: LensType) => {
    setLensType(type);
    setStep(type === 'normal' ? 3 : 2);
  };

  const handleEyePowerChange = (
    eye: keyof EyePower,
    measure: keyof EyePowerValue,
    value: string
  ) => {
    setEyePower((prev) => ({ ...prev, [eye]: { ...prev[eye], [measure]: value } }));
  };

  const handleSave = () => {
    storePrescriptionFile(cartItemId, prescriptionFile);
    setLensSelection(cartItemId, {
      lensType,
      coating,
      eyePower,
      hasPrescription: !!prescriptionFile || (initialData?.hasPrescription ?? false),
      pdMeasurement,
      leftPdMeasurement,
      rightPdMeasurement,
      coatingPrice: coating ? (COATING_PRICES[coating] ?? 0) : 0,
    });
    onClose();
  };

  const prevStep = () => {
    if (step === 3 && lensType === 'normal') { setStep(1); setLensType(''); }
    else setStep((p) => Math.max(p - 1, 1));
  };

  return (
    <div className="lens-modal-overlay" onClick={onClose}>
      <div className="lens-modal" onClick={(e) => e.stopPropagation()}>
        <button className="lens-modal__close" onClick={onClose} type="button">✕</button>

        <h2 className="lens-page__title" style={{ padding: '28px 24px 0' }}>
          Lenses for {productName}
        </h2>

        {/* Stepper */}
        <div className="lens-stepper">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className={`lens-step ${step > i ? 'lens-step--done' : ''} ${step === i + 1 ? 'lens-step--active' : ''}`}>
                <div className="lens-step__circle">{step > i + 1 ? '✓' : i + 1}</div>
                <span className="lens-step__label">{label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`lens-step__line ${step > i + 1 ? 'lens-step__line--done' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="lens-content">
          {/* Step 1: Lens Type */}
          {step === 1 && (
            <div className="lens-grid">
              {LENS_TYPES.map((type) => (
                <button
                  key={type}
                  className={`lens-card ${lensType === type ? 'lens-card--selected' : ''}`}
                  onClick={() => handleLensTypeSelect(type)}
                >
                  <div className="lens-card__img-wrap">
                    <img src={`/${type}.png`} alt={type} className="lens-card__img" />
                  </div>
                  <span className="lens-card__label">{lensLabels[type]}</span>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Eye Power */}
          {step === 2 && (
            <div className="lens-power">
              <div className="lens-power__eyes">
                {(['leftEye', 'rightEye'] as const).map((eye) => (
                  <div key={eye} className="lens-power__eye">
                    <h4 className="lens-power__eye-title">{eye === 'leftEye' ? 'Left Eye' : 'Right Eye'}</h4>
                    {(['sphere', 'cylinder', 'axis'] as const).map((measure) => (
                      <div key={measure} className="lens-power__row">
                        <label className="lens-power__label">
                          {measure.charAt(0).toUpperCase() + measure.slice(1)}
                        </label>
                        <SearchableSelect
                          options={measure === 'sphere' ? SPHERE_OPTIONS : measure === 'cylinder' ? CYLINDER_OPTIONS : AXIS_OPTIONS}
                          value={eyePower[eye][measure]}
                          placeholder="Select"
                          onChange={(val) => handleEyePowerChange(eye, measure, val)}
                          className="lens-power__select"
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* PD Measurement */}
              <div className="lens-power__pd" style={{ marginTop: '24px', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 className="lens-power__eye-title" style={{ margin: 0 }}>PD Measurement (Optional)</h4>
                  <button
                    type="button"
                    onClick={() => setIsPdPopupOpen(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '8px 12px',
                      backgroundColor: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '14px'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="2" y1="12" x2="22" y2="12"></line><line x1="6" y1="8" x2="6" y2="16"></line><line x1="18" y1="8" x2="18" y2="16"></line></svg>
                    Measure PD
                  </button>
                </div>
                <div className="lens-power__row" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <label className="lens-power__label" style={{ minWidth: '120px', flexShrink: 0 }}>I PD mm</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="e.g. 62.50"
                    value={pdMeasurement}
                    onChange={(e) => setPdMeasurement(e.target.value)}
                    style={{ flex: '1 1 100px', minWidth: '80px', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                </div>
                <div className="lens-power__row" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 150px' }}>
                    <label className="lens-power__label" style={{ minWidth: '60px' }}>L PD mm</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="31.25"
                      value={leftPdMeasurement}
                      onChange={(e) => setLeftPdMeasurement(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 150px' }}>
                    <label className="lens-power__label" style={{ minWidth: '60px' }}>R PD mm</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="31.25"
                      value={rightPdMeasurement}
                      onChange={(e) => setRightPdMeasurement(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                    />
                  </div>
                </div>
              </div>

              {/* Prescription Upload */}
              <div className="lens-power__upload">
                <div className={`lens-upload-box ${prescriptionFile ? 'lens-upload-box--filled' : ''}`}>
                  {prescriptionFile ? (
                    <>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.5">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                      <p style={{ color: '#16a34a', fontWeight: 600 }}>File Selected</p>
                      <span className="lens-upload-box__name">{prescriptionFile.name}</span>
                      <button className="lens-upload-box__remove" onClick={() => setPrescriptionFile(null)} type="button">
                        Remove
                      </button>
                    </>
                  ) : (
                    <>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <p>Upload Prescription (Optional)</p>
                      <label className="lens-upload-box__label">
                        Choose File
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          style={{ display: 'none' }}
                          onChange={(e) => setPrescriptionFile(e.target.files?.[0] || null)}
                        />
                      </label>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Coating */}
          {step === 3 && (
            <div className="lens-grid">
              {COATING_TYPES.map((coat) => (
                <button
                  key={coat}
                  className={`lens-card ${coating === coat ? 'lens-card--selected' : ''}`}
                  onClick={() => setCoating(coat)}
                >
                  <div className="lens-card__img-wrap">
                    <img src={`/${coat}.png`} alt={coat} className="lens-card__img" />
                  </div>
                  <span className="lens-card__label">{coatingLabels[coat]}</span>
                  <span className="lens-card__price">₹{COATING_PRICES[coat].toLocaleString()}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="lens-page__footer">
          {step > 1 && (
            <button className="btn btn--outline" onClick={prevStep}>Back</button>
          )}
          {step < STEPS.length ? (
            <button
              className="btn btn--primary"
              style={{ marginLeft: 'auto' }}
              onClick={() => setStep((p) => Math.min(p + 1, STEPS.length))}
              disabled={step === 1 && !lensType}
            >
              Next
            </button>
          ) : (
            <button
              className="btn btn--primary"
              style={{ marginLeft: 'auto' }}
              onClick={handleSave}
              disabled={!coating}
            >
              Save Selection
            </button>
          )}
        </div>
      </div>


      <PDMeasurementPopup
        isOpen={isPdPopupOpen}
        onClose={() => setIsPdPopupOpen(false)}
        onComplete={(result: any) => {
          if (result.leftPd && result.rightPd) {
            setLeftPdMeasurement(result.leftPd.toString());
            setRightPdMeasurement(result.rightPd.toString());
          } else {
            setPdMeasurement(result.pd.toString());
          }
          setIsPdPopupOpen(false);
        }}
      />
    </div>
  );
};


export default LensSelectionModal;
